var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useState } from 'react';
import { Amplify, Hub } from 'aws-amplify';
import { METRIC_EVENT_NAMES, TimingMetrics, initMetrics, trackCustomEvent } from '@amzn/austin-core';
import { getAwsConfig, getEnvironmentVariables } from '../modules/environment';
import { hasAccessTokens, redirectToOriginator, redirectToLoginPage } from './helpers/session';
function initializeMetrics() {
    var _a = getEnvironmentVariables(), METRICS_IDENTITY_POOL_ID = _a.METRICS_IDENTITY_POOL_ID, METRICS_IDENTITY_POOL_REGION = _a.METRICS_IDENTITY_POOL_REGION, METRICS_PINPOINT_APP_CLIENT_ID = _a.METRICS_PINPOINT_APP_CLIENT_ID, METRICS_PINPOINT_APP_REGION = _a.METRICS_PINPOINT_APP_REGION;
    var metricsConfig = {
        analyticsAwsConfig: {
            AWSPinpoint: {
                appId: METRICS_PINPOINT_APP_CLIENT_ID,
                mandatorySignIn: true,
                region: METRICS_PINPOINT_APP_REGION
            },
            Auth: {
                identityPoolId: METRICS_IDENTITY_POOL_ID,
                region: METRICS_IDENTITY_POOL_REGION
            }
        },
        autoTrackSessionsEnabled: false
    };
    initMetrics(metricsConfig);
}
/**
 * This component is responsible for 1) configuring Amplify by passing in the appropriate, environment-specific
 * AWS configuration and 2) verifying that the user has authenticated. Once we've verified that the current user
 * has authenticated and established a session, the web application (layout/module views) will be rendered. Up until
 * that happens `null` is returned as this prevents premature rendering and fetching of global data.
 */
export var AuthenticationCheck = function (props) {
    var _a = useState(false), signedIn = _a[0], setSignedIn = _a[1];
    var AMPLIFY_LOG_LEVEL = getEnvironmentVariables().AMPLIFY_LOG_LEVEL;
    var awsConfig = getAwsConfig();
    var metricsSessionStartedKey = 'metricsSessionStarted';
    var timingMetrics = TimingMetrics(METRIC_EVENT_NAMES.TIMING, true);
    var timingMetricsStartMarker = timingMetrics.start('AuthenticationCheck');
    /**
     * Immediately capture the requested URL if it doesn't include the "code" QS param which exists on the first redirect
     * from Cognito after authenticating. This is to address an issue with how AmplifyJS v4 (and later) handles invalid OAuth
     * sessions where it will redirect to Cognito to clean up invalid sessions before redirecting back into AUSTIN, resulting
     * in the original requested URL being lost when redirectToLoginPage() is invoked again.
     *
     * The redirect occurs when invoking Auth.currentAuthenticatedUser() (via getAccessTokens()) for the first time if the user
     * has not had an active session in the past 24 hours.
     *
     * @see https://github.com/aws-amplify/amplify-js/pull/9628
     * @see https://sim.amazon.com/issues/AUSTIN-31745
     *
     * @todo
     * We can try removing this once we're able to upgrade to AmplifyJS v6, which is currently not possible due to a bug with
     * cached credentials being sent when record() is invoked to send metrics to Pinpoint.
     *
     * @see https://github.com/aws-amplify/amplify-js/issues/12763
     */
    if (!window.location.href.includes('?code')) {
        sessionStorage.setItem('redirect_uri', window.location.href);
    }
    Amplify.Logger.LOG_LEVEL = AMPLIFY_LOG_LEVEL;
    // Amplify.Logger.LOG_LEVEL = 'DEBUG';
    Amplify.configure(awsConfig);
    /**
     * Persist the AWS config so that it can be accessed by other modules in the core library and/or micro-frontends.
     * As of this writing, the only place where this is needed is in the `api` module, specifically the `executeRequest()`
     * function that performs operations in AppSync via the Amplify library (which requires a valid AWS config). Since
     * the container (i.e. AustinStaticWebsite) and individual micro-frontends will use separate instances of the
     * `@amzn/austin-core` library, this allows the module to re-establish context.
     *
     * @see https://ui.austin.a2z.com/module-api.html
     */
    localStorage.setItem('aws-config', JSON.stringify(awsConfig));
    useEffect(function () {
        checkSessionTokens();
    }, []);
    function checkSessionTokens() {
        return __awaiter(this, void 0, void 0, function () {
            var attempts;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        attempts = 0;
                        return [4 /*yield*/, hasAccessTokens().then(function (hasTokenInitially) {
                                if (hasTokenInitially) {
                                    initializeSession();
                                }
                                else {
                                    // Check for token multiple times with a delay in case a sign in event happens
                                    var intervalId_1 = window.setInterval(function () {
                                        hasAccessTokens().then(function (hasToken) {
                                            if (hasToken) {
                                                initializeSession(intervalId_1);
                                            }
                                            else if (attempts > 30) {
                                                clearInterval(intervalId_1);
                                                redirectToLoginPage();
                                            }
                                            attempts += 1;
                                        });
                                    }, 100);
                                }
                            })];
                    case 1:
                        _a.sent();
                        Hub.listen('auth', function (_a) {
                            var event = _a.payload.event;
                            if (event === 'signOut') {
                                stopMetricsSession();
                                redirectToLoginPage();
                            }
                            else if (event === 'oAuthSignOut') {
                                /**
                                 * @note
                                 * This event is emitted when user manually signs out of application (via Auth.signOut).
                                 * After upgrading to AmplifyJS v6 this can be removed as it's no longer a support event type.
                                 */
                                stopMetricsSession();
                            }
                        });
                        return [2 /*return*/];
                }
            });
        });
    }
    function initializeSession(intervalId) {
        if (!signedIn) {
            redirectToOriginator();
            setSignedIn(true);
        }
        initializeMetrics();
        // if user is restarting the application and a previous session
        // was already started then send a session stop event
        if (localStorage.getItem(metricsSessionStartedKey)) {
            stopMetricsSession();
        }
        startMetricsSession();
        if (intervalId) {
            clearInterval(intervalId);
        }
        timingMetrics.stop(timingMetricsStartMarker);
    }
    function startMetricsSession() {
        var sessionStarted = localStorage.getItem(metricsSessionStartedKey);
        if (!sessionStarted) {
            trackCustomEvent({
                attributes: {
                    innerWidth: window.innerWidth,
                    screenWidth: window.screen.width
                },
                name: METRIC_EVENT_NAMES.SESSION_START
            });
            localStorage.setItem(metricsSessionStartedKey, 'true');
        }
    }
    function stopMetricsSession() {
        trackCustomEvent({
            name: METRIC_EVENT_NAMES.SESSION_STOP
        });
        localStorage.removeItem(metricsSessionStartedKey);
    }
    return signedIn ? React.cloneElement(props.children) : null;
};
